import React from 'react'
import {
  Route,
  Redirect,
  Switch,
  withRouter,
  useHistory,
} from 'react-router-dom'
import AcceptMembershipPanel from './AcceptMembershipPanel'
import SignUpAndAcceptMembershipPanel, {
  SignUpAndAcceptMembershipPanelProps,
} from './SignUpAndAcceptMembershipPanel'
import SignInAndAcceptMembershipPanel from './SignInAndAcceptMembershipPanel'
import SwitchAccountOrAcceptMembershipPanel from './SwitchAccountOrAcceptMembershipPanel'
import ForgotPasswordPanel from './ForgotPasswordPanel'
import { routes } from '@sketch/constants'
import { Location, History } from 'history'
import { IsSignedIn, IsNotSignedIn } from 'containers/IsSignedIn'
import {
  useGetShareMembershipQuery,
  useShareRequestAccessMutation,
} from '@sketch/gql-types'
import { withToastConsumer, ToastContextValues, useToast } from '@sketch/toasts'
import NotFoundView from 'views/NotFoundView'
import InvalidInvitationView from 'views/InvalidInvitationView'
import * as Sentry from '@sentry/browser'
import jwtDecode from 'jwt-decode'
import { Flex, LoadingState } from '@sketch/components'
import queryString from 'query-string'
import useUserProfile from 'modules/user/useUserProfile'
import useAcceptShareMembershipInvitation from '../operations/useAcceptShareMembershipInvitation'
import { noop } from '@sketch/utils'
import useSignOut from 'modules/user/useSignOut'
import { StyledIndexLayout } from './shared'

const useInviteToken = (queryToken: string | string[] | null) => {
  let inviteToken: string | null = null
  let shareShortId: string | null = null
  let documentName: string | null = null

  if (queryToken) {
    inviteToken = Array.isArray(queryToken) ? queryToken[0] : queryToken
    try {
      // eslint-disable-next-line no-extra-semi
      ;({
        document_name: documentName,
        share_short_id: shareShortId,
      } = jwtDecode(inviteToken))
    } catch {
      inviteToken = shareShortId = documentName = null
    }
  }

  return { inviteToken, documentName, shareShortId }
}

interface AcceptShareInviteByInviteTokenViewProps {
  showToast?: ToastContextValues['showToast']
  history: History
  location: Location
}

interface AcceptInviteProps extends SignUpAndAcceptMembershipPanelProps {
  signOut: () => void
  invitedEmail: string
}
/**
 * We needed to move the routes logic into a separate component because
 * the component that was encapsulating all the logic wasn't updating
 * the user profile. Accesing it from this component works perfectly
 */
const AcceptInviteRoute = ({
  signOut,
  invitedEmail,
  ...acceptShareMembershipPanelProps
}: AcceptInviteProps) => {
  const history = useHistory()
  const { showToast } = useToast()
  const { data: profile } = useUserProfile()

  const [requestShareAccess] = useShareRequestAccessMutation({
    redirectErrors: true,
    variables: { shortId: acceptShareMembershipPanelProps.shareShortId },
    onError: ({ message }) => {
      showToast(message, 'negative')
      /* Redirect home of the user already requested access before */
      if (message === "You've already requested access") {
        history.push(routes.ENTRY.create({}))
      }
    },
    onCompleted: () => {
      showToast('Request has been sent')
      /* Redirect home instead of showing the ShareAuthorizer NotFound */
      history.push(routes.ENTRY.create({}))
    },
  })

  const userEmail = profile?.me.email
  const userAvatar = profile?.me.avatar?.small
  const isSSOAccount = !profile?.me.hasPersonalIdentity
  const isCurrentUserInvited = invitedEmail === userEmail

  // Just to get rid of TS errors
  if (!userEmail) return null

  if (isCurrentUserInvited && !isSSOAccount) {
    return (
      <AcceptMembershipPanel
        resourceType="document"
        userInfo={{
          userEmail,
          userAvatar,
        }}
        onRequestAccessWithDifferentAccount={signOut}
        {...acceptShareMembershipPanelProps}
      />
    )
  }

  return (
    <SwitchAccountOrAcceptMembershipPanel
      resourceType="document"
      invitedEmail={invitedEmail}
      userInfo={{
        userEmail,
        userAvatar,
      }}
      onRequestAccess={requestShareAccess}
      {...acceptShareMembershipPanelProps}
    />
  )
}

const AcceptShareInviteByInviteTokenView = (
  props: AcceptShareInviteByInviteTokenViewProps
) => {
  const { showToast = noop, history, location } = props

  const query = queryString.parse(location.search)
  const queryToken = query.token

  const { inviteToken, documentName, shareShortId } = useInviteToken(
    queryToken as string | string[] | null
  )

  const signOut = useSignOut({
    location: {
      pathname: routes.SHARE_ACCEPT_INVITE_SIGN_UP.create({
        query,
      }),
      search: location.search,
      state: {
        from: location,
      },
    },
    reason: 'Switching account to accept share invitation',
  })

  if (!inviteToken) {
    Sentry.captureMessage('Failed to parse invite token')
  }

  const { data, error, loading } = useGetShareMembershipQuery({
    variables: { token: inviteToken },
    skip: !inviteToken,
  })

  const [
    acceptMembership,
    { loading: acceptMembershipLoading, error: acceptMembershipError },
  ] = useAcceptShareMembershipInvitation({
    variables: { token: inviteToken },
    onCompleted: () => {
      showToast('Invite successfully accepted')
      history.push(routes.SHARE_VIEW.create({ shareID: shareShortId ?? '' }))
    },
    onError: 'do-nothing', // we use acceptShareMembershipError to show the error message
  })

  if (loading) {
    return <LoadingState />
  }

  if (!inviteToken || !shareShortId || !data || error) {
    return <NotFoundView />
  }

  const { shareMembership } = data

  if (!shareMembership) {
    return <InvalidInvitationView />
  }

  const invitedEmail = shareMembership?.email ?? ''

  if (shareMembership?.inviteStatus === 'ACCEPTED' && shareShortId !== null)
    return (
      <Redirect
        to={routes.SHARE_VIEW.create({
          shareID: shareShortId,
        })}
      />
    )

  const acceptShareMembershipPanelProps = {
    shareShortId,
    acceptMembership,
    acceptMembershipError,
    acceptMembershipLoading,
    inviter: shareMembership?.inviter,
    inviteToken,
    resourceName: documentName,
    invitedEmail,
  } as SignUpAndAcceptMembershipPanelProps

  return (
    <StyledIndexLayout variant="teal" headerLink="none">
      <Flex
        maxWidth="512px"
        width="100%"
        alignSelf="center"
        flex="1"
        alignItems="center"
      >
        <Switch>
          <Route path={routes.SHARE_ACCEPT_INVITE_FORGOT_PASSWORD.template()}>
            <ForgotPasswordPanel invitedEmail={invitedEmail} />
          </Route>
          <Route path={routes.SHARE_ACCEPT_INVITE_SIGN_UP.template()}>
            <SignUpAndAcceptMembershipPanel
              {...acceptShareMembershipPanelProps}
            />
          </Route>
          <Route path={routes.SHARE_ACCEPT_INVITE.template()}>
            <IsSignedIn>
              <AcceptInviteRoute
                signOut={signOut}
                {...acceptShareMembershipPanelProps}
              />
            </IsSignedIn>
            <IsNotSignedIn>
              <Redirect
                to={{
                  pathname: routes.SHARE_ACCEPT_INVITE_SIGN_IN.template(),
                  search: location.search,
                }}
              />
            </IsNotSignedIn>
          </Route>
          <Route path={routes.SHARE_ACCEPT_INVITE_SIGN_IN.template()}>
            <SignInAndAcceptMembershipPanel
              {...acceptShareMembershipPanelProps}
            />
          </Route>
          <Redirect
            to={{
              pathname: routes.SHARE_ACCEPT_INVITE.template(),
              search: location.search,
            }}
          />
        </Switch>
      </Flex>
    </StyledIndexLayout>
  )
}

export default withToastConsumer(withRouter(AcceptShareInviteByInviteTokenView))
